import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient, { DashboardStats } from "../../services/api"; // Corrected: Assuming apiClient is default export and import DashboardStats
import type { RootState } from "../../app/store";
// import { AuthUser } from '../auth/authSlice';

// Interfaces (ensure these match backend responses and frontend needs)
export interface FetchRequisitionsParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    store_ids?: number[];
    category_id?: number | null;
    area_coach_id?: number | null;
    showJobCardRequiredOnly?: boolean;
}

export interface RejectRequisitionPayload {
    comment: string;
}

export interface MinimalStore {
    storenumber?: string;
    id: number;
    name: string;
}

export interface MinimalUser {
    id: number;
    name: string;
}

export interface Approver {
    id?: number;
    user_id: number;
    user: MinimalUser;
    status_id: number;
    comment?: string;
    comments?: string;
}

export interface AssetItem {
    id?: number;
    asset: string;
    descriptions?: DescriptionItem[];
}

export interface DescriptionItem {
    id?: number;
    description: string;
}

export interface RepairItem {
    id?: number;
    description: string;
}

export interface EquipmentRequestItem {
    id?: number;
    equipment_id?: number; // Snake_case from API
    equipment_smallware_id?: number;
    equipment?: {
        id: number;
        description: string;
        category?: { id: number; name: string };
        subCategory?: { id: number; name: string };
        serial_number?: string;
    };
    notes?: string;
}

export interface ProcessedEquipmentItem {
    asset: string;
    equipment: {
        category: string;
        subcategory: string;
        serial_number: string;
    };
    descriptions: string[];
}

export interface ProcessedAssetDescriptionPair {
    asset: string;
    descriptions: string[];
}

export interface HistoryEntry {
    id?: number;
    user_id: number;
    user?: MinimalUser;
    action: string;
    comment?: string;
    created_at: string;
}

export interface PictureItem {
    id?: number;
    attachment: string;
    comment?: string;
}

// Define the structure for a reply item based on your Blade template
export interface ReplyItem {
    id?: number; // Or string, if IDs are not numbers
    user: MinimalUser; // Assuming it has a nested user object with name
    content: string;
    created_at: string; // Assuming it's a string timestamp
    // Add any other properties that a reply object might have
}

export interface RequisitionListItem {
    id: number;
    date: string;
    status_id: number;
    submitted: boolean | number;
    internal?: boolean | number;
    stores: MinimalStore[];
    category?: { id: number; name: string };
    subcategory?: { id: number; name: string };
    user: MinimalUser;
    supplier?: { id: number; name: string };
    technician?: MinimalUser;
    approvers?: Approver[];
    updated_at: string;
    order?: {
        id: number;
        order_number?: string;
        reference_number?: string;
        jobCards?: JobCard[];
        jobcards?: JobCard[];
        job_cards?: JobCard[];
    };
    status?: { id: number; name: string; code?: string };
    quotation?: {
        id: number;
        amount: number;
        vat?: boolean | number;
        attachment?: string;
    };
    jobCard?: JobCard;
    jobCards?: JobCard[];
    created_by?: number; // Creator ID for edit/delete authorization
    user_id?: number;
}

export interface RequisitionDetail extends RequisitionListItem {
    supplier?: { id: number; name: string };
    technician?: MinimalUser;
    assets?: AssetItem[];
    repairs?: RepairItem[];
    equipmentRequests?: EquipmentRequestItem[];
    equipment_requests?: EquipmentRequestItem[]; // Snake_case from API
    histories?: HistoryEntry[];
    pictures?: PictureItem[];
    internal: number;
    is_repair?: number; // Add is_repair property
    replies?: ReplyItem[]; // Add replies here
    quotation?: {
        id: number;
        amount: number;
        date?: string;
        quotation_number?: string;
        vat?: boolean | number;
        attachment?: string;
    }; // Add quotation here
    equipmentItems?: ProcessedEquipmentItem[]; // Add processed equipment data
    assetDescriptionPairs?: ProcessedAssetDescriptionPair[]; // Add processed asset pairs
    jobCard?: JobCard;
    jobCards?: JobCard[];
}

export interface JobCard {
    id?: number;
    job_number?: string;
    attachment?: string;
    created_at?: string;
}

export interface PaginatedRequisitionsResponse {
    current_page: number;
    data: RequisitionListItem[];
    first_page_url: string;
    from: number | null;
    last_page: number;
    last_page_url: string;
    links: any[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number | null;
    total: number;
}

export interface AddHistoryPayload {
    requisitionId: string;
    comment: string;
    action?: string;
}

// New payload interface for adding a comment (reply)
export interface AddCommentPayload {
    requisitionId: string;
    content: string;
}

export interface CreateRequisitionPayload {
    category: number;
    sub_category: number;
    internal: number;
    is_repair: number;
    selectedStores: number[];
    assetsToRepair: string[];
    descriptionOfRepairs: string[];
    selected_equipment?: Array<{
        id: number;
        description: string;
        category: string;
        subcategory: string;
        serial_number?: string;
    }>;
    equipment_descriptions?: string[];
    hasQuotation?: boolean;
    quotation_date?: string;
    repair_cost?: number;
    quotation_number?: string;
    vat?: number;
    attachment?: File | null;
    requisition_date?: string;
    technician_id?: number | null;
    supplier_id?: number | null;
}

export interface CreateRequisitionResponse {
    message: string;
    requisition: RequisitionDetail;
}

interface RequisitionState {
    list: RequisitionListItem[];
    selectedRequisition: RequisitionDetail | null;
    pagination: PaginatedRequisitionsResponse | null;
    isLoadingList: boolean;
    isLoadingDetails: boolean;
    isUpdating: boolean;
    error: string | null | unknown;
    filters: FetchRequisitionsParams;
    dashboardStats: DashboardStats | null;
    isLoadingDashboardStats: boolean;
    dashboardError: string | null;
}

const initialFilters: FetchRequisitionsParams = {
    status: "pending_my_approval",
    page: 1,
    per_page: 15,
    search: "",
    date_from: "",
    date_to: "",
    showJobCardRequiredOnly: false,
};

const initialState: RequisitionState = {
    list: [],
    selectedRequisition: null,
    pagination: null,
    isLoadingList: false,
    isLoadingDetails: false,
    isUpdating: false,
    error: null,
    filters: initialFilters,
    dashboardStats: null,
    isLoadingDashboardStats: false,
    dashboardError: null,
};

// Async Thunks
export const fetchRequisitionsList = createAsyncThunk<
    PaginatedRequisitionsResponse, // Return type
    FetchRequisitionsParams | undefined, // Argument type (undefined for no params explicitly passed)
    { state: RootState; rejectValue: string } // ThunkApi config
>("requisitions/fetchList", async (params, { getState, rejectWithValue }) => {
    try {
        const currentFilters = getState().requisitions.filters;
        const mergedParams = { ...currentFilters, ...(params || {}) }; // Ensure params is an object

        // Remove client-side filter parameters from API request
        const apiParams = { ...mergedParams };
        delete apiParams.area_coach_id; // Area coach filtering is done client-side
        delete apiParams.showJobCardRequiredOnly; // Job card filtering is done client-side

        // Optimize page size based on filters
        if (
            mergedParams.area_coach_id ||
            mergedParams.showJobCardRequiredOnly
        ) {
            // Request a larger page size for client-side filtering
            apiParams.per_page = 30; // Reduced from 50 for faster initial load
        } else {
            // Standard page size for normal requests
            apiParams.per_page = 20; // Reduced from default for faster loading
        }

        const response = await apiClient.get("/requisitions", {
            params: apiParams,
        });
        return response.data as PaginatedRequisitionsResponse;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to fetch requisitions"
        );
    }
});

export const fetchRequisitionDetails = createAsyncThunk<
    RequisitionDetail,
    string, // Argument is id (string)
    { rejectValue: string }
>("requisitions/fetchDetails", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.get(`/requisitions/${id}`);
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to fetch requisition details"
        );
    }
});

export const approveRequisitionAction = createAsyncThunk<
    RequisitionDetail,
    string, // Argument is id (string)
    { rejectValue: string }
>("requisitions/approve", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/approver/requisitions/${id}/approve`
        );
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to approve requisition"
        );
    }
});

export const rejectRequisitionAction = createAsyncThunk<
    RequisitionDetail,
    { id: string; data: RejectRequisitionPayload }, // Argument type
    { rejectValue: string }
>("requisitions/reject", async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/approver/requisitions/${id}/reject`,
            data
        );
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to reject requisition"
        );
    }
});

export const addRequisitionHistoryEntry = createAsyncThunk<
    HistoryEntry, // Return type: the newly created history entry
    AddHistoryPayload, // Argument type
    { state: RootState; rejectValue: string } // ThunkApi config
>("requisitions/addHistory", async (payload, { getState, rejectWithValue }) => {
    try {
        // This thunk might not be directly called from UI anymore if comments handle history.
        // Or, it can be used for specific non-comment history entries.
        const response = await apiClient.post(
            `/requisitions/${payload.requisitionId}/history`,
            payload
        );
        return response.data as HistoryEntry;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to add history entry"
        );
    }
});

// Add bulk approve and bulk reject thunks
export const bulkApproveRequisitions = createAsyncThunk<
    { message: string; updatedIds: number[] },
    number[], // Array of requisition IDs
    { rejectValue: string }
>("requisitions/bulkApprove", async (ids, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            "/approver/requisitions/bulk-approve",
            { ids }
        );
        return response.data as { message: string; updatedIds: number[] };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to bulk approve requisitions"
        );
    }
});

export const bulkRejectRequisitions = createAsyncThunk<
    { message: string; updatedIds: number[] },
    { ids: number[]; comment: string }, // Array of requisition IDs and rejection comment
    { rejectValue: string }
>("requisitions/bulkReject", async ({ ids, comment }, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            "/approver/requisitions/bulk-reject",
            { ids, comment }
        );
        return response.data as { message: string; updatedIds: number[] };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to bulk reject requisitions"
        );
    }
});

// Thunk to add a comment (reply) to a requisition
export const addRequisitionCommentAction = createAsyncThunk<
    ReplyItem, // Return type: the newly created reply item
    AddCommentPayload, // Argument type
    { rejectValue: string }
>("requisitions/addComment", async (payload, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/requisitions/${payload.requisitionId}/comments`,
            {
                content: payload.content,
            }
        );
        return response.data.reply as ReplyItem; // Assuming backend returns { reply: ReplyItem, history?: HistoryEntry }
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to add comment (thunk)"
        );
    }
});

export const createRequisitionAction = createAsyncThunk<
    CreateRequisitionResponse,
    CreateRequisitionPayload,
    { rejectValue: string }
>("requisitions/create", async (payload, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        formData.append("category", String(payload.category));
        formData.append("sub_category", String(payload.sub_category));
        formData.append("internal", String(payload.internal));
        formData.append("is_repair", String(payload.is_repair));
        payload.selectedStores.forEach((id) =>
            formData.append("selectedStores[]", String(id))
        );
        payload.assetsToRepair.forEach((asset) =>
            formData.append("assetsToRepair[]", asset)
        );
        payload.descriptionOfRepairs.forEach((desc) =>
            formData.append("descriptionOfRepairs[]", desc)
        );
        if (payload.selected_equipment) {
            payload.selected_equipment.forEach((eq, index) => {
                formData.append(`selected_equipment[${index}][id]`, String(eq.id));
                formData.append(`selected_equipment[${index}][description]`, eq.description);
                formData.append(`selected_equipment[${index}][category]`, eq.category);
                formData.append(`selected_equipment[${index}][subcategory]`, eq.subcategory);
                if (eq.serial_number) {
                    formData.append(`selected_equipment[${index}][serial_number]`, eq.serial_number);
                }
            });
        }
        if (payload.equipment_descriptions) {
            payload.equipment_descriptions.forEach((desc) =>
                formData.append("equipment_descriptions[]", desc)
            );
        }
        if (payload.hasQuotation !== undefined) {
            formData.append("hasQuotation", String(payload.hasQuotation ? 1 : 0));
        }
        if (payload.quotation_date) {
            formData.append("quotation_date", payload.quotation_date);
        }
        if (payload.repair_cost !== undefined && payload.repair_cost > 0) {
            formData.append("repair_cost", String(payload.repair_cost));
        }
        if (payload.quotation_number) {
            formData.append("quotation_number", payload.quotation_number);
        }
        if (payload.vat !== undefined) {
            formData.append("vat", String(payload.vat));
        }
        if (payload.attachment) {
            formData.append("attachment", payload.attachment);
        }
        if (payload.requisition_date) {
            formData.append("requisition_date", payload.requisition_date);
        }
        if (payload.technician_id) {
            formData.append("technician_id", String(payload.technician_id));
        }
        if (payload.supplier_id) {
            formData.append("supplier_id", String(payload.supplier_id));
        }

        const response = await apiClient.post("/requisitions", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data as CreateRequisitionResponse;
    } catch (error: any) {
        const errorData = error.response?.data;
        if (errorData?.errors) {
            const errorMessages = Object.entries(errorData.errors)
                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                .join('\n');
            return rejectWithValue(`Validation errors:\n${errorMessages}`);
        }
        return rejectWithValue(
            errorData?.message || "Failed to create requisition"
        );
    }
});

export const submitRequisitionAction = createAsyncThunk<
    RequisitionDetail,
    string,
    { rejectValue: string }
>("requisitions/submit", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(`/requisitions/${id}/submit`);
        return response.data.requisition as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to submit requisition"
        );
    }
});

export const resendNotificationAction = createAsyncThunk<
    { message: string; approver: string },
    string,
    { rejectValue: string }
>("requisitions/resendNotification", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(`/requisitions/${id}/resend-notification`);
        return response.data as { message: string; approver: string };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to resend notification"
        );
    }
});

export const deleteRequisitionAction = createAsyncThunk<
    { message: string; id: number },
    string,
    { rejectValue: string }
>("requisitions/delete", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.delete(`/requisitions/${id}`);
        return { message: response.data.message, id: Number(id) };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to delete requisition"
        );
    }
});

export const addRequisitionPictureAction = createAsyncThunk<
    PictureItem,
    { requisitionId: string; picture: File; comment: string },
    { rejectValue: string }
>("requisitions/addPicture", async (payload, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        formData.append("picture", payload.picture);
        formData.append("comment", payload.comment);
        const response = await apiClient.post(
            `/requisitions/${payload.requisitionId}/pictures`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return response.data.picture as PictureItem;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to upload picture"
        );
    }
});

export const deleteRequisitionPictureAction = createAsyncThunk<
    { id: number },
    { requisitionId: string; pictureId: number },
    { rejectValue: string }
>("requisitions/deletePicture", async (payload, { rejectWithValue }) => {
    try {
        await apiClient.delete(
            `/requisitions/${payload.requisitionId}/pictures/${payload.pictureId}`
        );
        return { id: payload.pictureId };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to delete picture"
        );
    }
});

// New Thunk for fetching dashboard stats
export const fetchDashboardStatsAction = createAsyncThunk<
    DashboardStats,
    void, // No arguments needed
    { rejectValue: string }
>("requisitions/fetchDashboardStats", async (_, { rejectWithValue }) => {
    try {
        const response = await apiClient.get("/approver/dashboard-stats");
        return response.data as DashboardStats;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to fetch dashboard statistics"
        );
    }
});

export const requisitionSlice = createSlice({
    name: "requisitions",
    initialState,
    reducers: {
        clearRequisitionError: (state: RequisitionState) => {
            state.error = null;
        },
        clearSelectedRequisition: (state: RequisitionState) => {
            state.selectedRequisition = null;
        },
        setFilters: (
            state: RequisitionState,
            action: PayloadAction<Partial<FetchRequisitionsParams>>
        ) => {
            state.filters = { ...state.filters, ...action.payload, page: 1 }; // Reset page to 1 on filter change
            // If pagination exists and a page is part of the payload, update current_page
            if (state.pagination && typeof action.payload.page === "number") {
                state.pagination.current_page = action.payload.page;
            }
        },
        updateRequisitionFilters: (
            state: RequisitionState,
            action: PayloadAction<Partial<FetchRequisitionsParams>>
        ) => {
            state.filters = { ...state.filters, ...action.payload, page: 1 }; // Reset page to 1 on filter change
            state.pagination = null; // Clear pagination as new filter will fetch new data
        },
        resetRequisitionFilters: (state: RequisitionState) => {
            state.filters = initialFilters;
            state.pagination = null;
        },
        addOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<ReplyItem>
        ) => {
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                state.selectedRequisition.replies.unshift(action.payload); // Add to the beginning for newest first
            } else if (state.selectedRequisition) {
                state.selectedRequisition.replies = [action.payload];
            }
        },
        removeOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<number | string>
        ) => {
            // Assuming reply ID is number or string
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                state.selectedRequisition.replies =
                    state.selectedRequisition.replies.filter(
                        (reply) => reply.id !== action.payload
                    );
            }
        },
        updateOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<ReplyItem>
        ) => {
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                const index = state.selectedRequisition.replies.findIndex(
                    (reply) => reply.id === action.payload.id
                );
                if (index !== -1) {
                    state.selectedRequisition.replies[index] = action.payload;
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                fetchRequisitionsList.pending,
                (state: RequisitionState) => {
                    state.isLoadingList = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchRequisitionsList.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<PaginatedRequisitionsResponse>
                ) => {
                    state.isLoadingList = false;
                    // If it's the first page, replace the list. Otherwise, append.
                    if (action.payload.current_page === 1) {
                        state.list = action.payload.data;
                    } else {
                        // Prevent duplicates if a page is somehow fetched multiple times
                        const newItems = action.payload.data.filter(
                            (newItem) =>
                                !state.list.find(
                                    (existingItem) =>
                                        existingItem.id === newItem.id
                                )
                        );
                        state.list = [...state.list, ...newItems];
                    }
                    state.pagination = action.payload;
                }
            )
            .addCase(
                fetchRequisitionsList.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingList = false;
                    state.error =
                        action.payload || "Failed to load requisitions";
                }
            )
            .addCase(
                fetchRequisitionDetails.pending,
                (state: RequisitionState) => {
                    state.isLoadingDetails = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchRequisitionDetails.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isLoadingDetails = false;
                    state.selectedRequisition = action.payload;
                }
            )
            .addCase(
                fetchRequisitionDetails.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingDetails = false;
                    state.error =
                        action.payload || "Failed to load requisition details";
                }
            )
            .addCase(
                approveRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                approveRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload; // Update selected with new data
                    // Optionally update list item if present
                    const index = state.list.findIndex(
                        (req) => req.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.list[index] = {
                            ...state.list[index],
                            ...action.payload,
                        };
                    }
                }
            )
            .addCase(
                approveRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload || "Failed to approve requisition";
                }
            )
            .addCase(
                rejectRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                rejectRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload; // Update selected with new data
                    // Optionally update list item if present
                    const index = state.list.findIndex(
                        (req) => req.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.list[index] = {
                            ...state.list[index],
                            ...action.payload,
                        };
                    }
                }
            )
            .addCase(
                rejectRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload || "Failed to reject requisition";
                }
            )
            .addCase(
                addRequisitionHistoryEntry.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                }
            )
            .addCase(
                addRequisitionHistoryEntry.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<HistoryEntry>
                ) => {
                    state.isUpdating = false;
                    if (
                        state.selectedRequisition &&
                        state.selectedRequisition.histories
                    ) {
                        state.selectedRequisition.histories.unshift(
                            action.payload
                        );
                    } else if (state.selectedRequisition) {
                        state.selectedRequisition.histories = [action.payload];
                    }
                }
            )
            .addCase(
                addRequisitionHistoryEntry.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload ?? "Failed to add comment";
                }
            )
            // Bulk approve cases
            .addCase(
                bulkApproveRequisitions.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                bulkApproveRequisitions.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{
                        message: string;
                        updatedIds: number[];
                    }>
                ) => {
                    state.isUpdating = false;
                    // Update requisitions in the list that were bulk approved
                    if (state.pagination && state.pagination.data) {
                        state.pagination.data = state.pagination.data.map(
                            (req) => {
                                if (
                                    action.payload.updatedIds.includes(req.id)
                                ) {
                                    return { ...req, status_id: 2 }; // Set status to approved
                                }
                                return req;
                            }
                        );
                    }
                }
            )
            .addCase(
                bulkApproveRequisitions.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload ?? "Failed to bulk approve requisitions";
                }
            )
            // Bulk reject cases
            .addCase(
                bulkRejectRequisitions.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                bulkRejectRequisitions.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{
                        message: string;
                        updatedIds: number[];
                    }>
                ) => {
                    state.isUpdating = false;
                    // Update requisitions in the list that were bulk rejected
                    if (state.pagination && state.pagination.data) {
                        state.pagination.data = state.pagination.data.map(
                            (req) => {
                                if (
                                    action.payload.updatedIds.includes(req.id)
                                ) {
                                    return { ...req, status_id: 3 }; // Set status to rejected
                                }
                                return req;
                            }
                        );
                    }
                }
            )
            .addCase(
                bulkRejectRequisitions.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload ?? "Failed to bulk reject requisitions";
                }
            )
            .addCase(
                addRequisitionCommentAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true; // or a specific isSubmittingComment state
                    state.error = null;
                }
            )
            .addCase(
                addRequisitionCommentAction.fulfilled,
                (state: RequisitionState, action: PayloadAction<ReplyItem>) => {
                    state.isUpdating = false;
                    if (state.selectedRequisition) {
                        // Prepend new comment to existing replies for immediate UI update
                        const newReply = action.payload;
                        state.selectedRequisition.replies = [
                            newReply,
                            ...(state.selectedRequisition.replies || []),
                        ];

                        // Also, add to history if the backend structures the comment as a history entry as well
                        // This depends on your API response for addRequisitionCommentAction
                        // For now, let's assume the comment is primarily a "reply"
                    }
                }
            )
            .addCase(
                addRequisitionCommentAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to add comment";
                }
            )
            // Add cases for fetchDashboardStatsAction
            .addCase(
                fetchDashboardStatsAction.pending,
                (state: RequisitionState) => {
                    state.isLoadingDashboardStats = true;
                    state.dashboardError = null;
                }
            )
            .addCase(
                fetchDashboardStatsAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<DashboardStats>
                ) => {
                    state.isLoadingDashboardStats = false;
                    state.dashboardStats = action.payload;
                }
            )
            .addCase(
                fetchDashboardStatsAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingDashboardStats = false;
                    state.dashboardError =
                        action.payload || "Failed to fetch dashboard stats";
                }
            )
            .addCase(
                createRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                createRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<CreateRequisitionResponse>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload.requisition;
                    state.list.unshift(action.payload.requisition as RequisitionListItem);
                }
            )
            .addCase(
                createRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to create requisition";
                }
            )
            .addCase(
                submitRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                submitRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload;
                    const index = state.list.findIndex(
                        (req) => req.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.list[index] = {
                            ...state.list[index],
                            ...action.payload,
                        };
                    }
                }
            )
            .addCase(
                submitRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to submit requisition";
                }
            )
            .addCase(
                deleteRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                deleteRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{ message: string; id: number }>
                ) => {
                    state.isUpdating = false;
                    state.list = state.list.filter(
                        (req) => req.id !== action.payload.id
                    );
                    if (state.selectedRequisition?.id === action.payload.id) {
                        state.selectedRequisition = null;
                    }
                }
            )
            .addCase(
                deleteRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to delete requisition";
                }
            )
            .addCase(
                addRequisitionPictureAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                addRequisitionPictureAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<PictureItem>
                ) => {
                    state.isUpdating = false;
                    if (state.selectedRequisition) {
                        state.selectedRequisition.pictures = [
                            action.payload,
                            ...(state.selectedRequisition.pictures || []),
                        ];
                    }
                }
            )
            .addCase(
                addRequisitionPictureAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to upload picture";
                }
            )
            .addCase(
                deleteRequisitionPictureAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                deleteRequisitionPictureAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{ id: number }>
                ) => {
                    state.isUpdating = false;
                    if (state.selectedRequisition && state.selectedRequisition.pictures) {
                        state.selectedRequisition.pictures =
                            state.selectedRequisition.pictures.filter(
                                (pic) => pic.id !== action.payload.id
                            );
                    }
                }
            )
            .addCase(
                deleteRequisitionPictureAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to delete picture";
                }
            );
    },
});

export const {
    clearSelectedRequisition,
    clearRequisitionError,
    setFilters,
    updateRequisitionFilters,
    resetRequisitionFilters,
    addOptimisticReply,
    removeOptimisticReply,
    updateOptimisticReply,
} = requisitionSlice.actions;

export default requisitionSlice.reducer;
